import * as moment from '@node_modules/moment';
import { SimpleDuration } from '@shared/models/simple-duration.model';
import { isoStringRegex } from '@shared/constants/regex-consts';
import { DictionaryDto } from '@shared/service-proxies/service-proxies';
import { weekdaysMapping } from '@shared/constants/weekdays-mapping.const';
import { cloneDeep } from 'lodash-es';

export class DateTimeHelper {
    static buildApiDateString(date: Date): string {
        const timezoneHourOffset = Math.abs(date.getTimezoneOffset());
        const momentDate = moment.utc(date).add(timezoneHourOffset, 'minutes');
        return momentDate.format('YYYY-MM-DD');
    }

    static buildApiDateTimeString(date: Date): string {
        const timezoneHourOffset = Math.abs(date.getTimezoneOffset());
        const momentDate = moment.utc(date).add(timezoneHourOffset, 'minutes');
        return momentDate.format('YYYY-MM-DDTHH:mm');
    }

    static utcApiDateTimeString(date: Date): string {
        const momentDate = moment.utc(date);
        return momentDate.format('YYYY-MM-DDTHH:mm');
    }

    static formatMomentDate(date: Date): moment.Moment {
        return date && moment(DateTimeHelper.buildApiDateString(date)).startOf('day');
    }

    // Converte una Date locale in un momento UTC midnight usando le componenti della data locale,
    // evitando lo shift di timezone che avviene con toISOString() sui momenti locali.
    static buildMomentDateUtc(date: Date): moment.Moment {
        return date && moment.utc(DateTimeHelper.buildApiDateString(date));
    }

    static formatMomentDateTime(date: Date): moment.Moment {
        return date && moment(DateTimeHelper.buildApiDateTimeString(date));
    }

    static formatMomentDateForForm(momentDate: moment.Moment): Date {
        const hours = momentDate?.hours() ?? 0;
        const minutes = momentDate?.minutes() ?? 0;
        const date = momentDate.toDate();
        date.setHours(hours, minutes);
        return date;
    }

    static formatMomentTimeForForm(momentDate: moment.Moment): Date {
        const date = new Date();
        const hours = momentDate?.hours() ?? 0;
        const minutes = momentDate?.minutes() ?? 0;
        date.setHours(hours, minutes);
        return date;
    }

    static minutesToSimpleDuration(totalMinutes: number): SimpleDuration {
        if (totalMinutes) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return { hours, minutes };
        } else {
            return undefined;
        }
    }

    static datesToMinutesDuration(start: Date, end: Date, precise = false): number {
        const momentStart = moment(start);
        const momentEnd = moment(end);
        let minutes = Math.abs(momentEnd.diff(momentStart, 'minute', precise));

        if (precise) {
            minutes = Math.round(minutes);
        }

        return minutes;
    }

    static validDate(value: string | Date): boolean {
        const regexPassed = isoStringRegex.test(value instanceof Date ? value.toISOString() : value);
        if (!regexPassed) {
            return false;
        }
        const testDate = new Date(value);
        const validTime = !isNaN(testDate.getTime());
        const validISOConversion = testDate.toISOString() === (value instanceof Date ? value.toISOString() : value);
        return validTime && validISOConversion;
    }

    static betweenMomentTimes(startDateTime: moment.Moment, endDateTime?: moment.Moment): boolean {
        const today = moment(new Date());
        const timezoneHourOffset = Math.abs(new Date().getTimezoneOffset());
        const now = moment({
            h: today.hours(),
            m: today.minutes(),
        });
        const startTime = moment({
            h: startDateTime.hour(),
            m: startDateTime.minute(),
        }).subtract(timezoneHourOffset, 'minutes');
        if (endDateTime) {
            const endTime = moment({
                h: endDateTime?.hour(),
                m: endDateTime?.minute(),
            }).subtract(timezoneHourOffset, 'minutes');
            return startTime.isSameOrBefore(now) && endTime.isAfter(now);
        }
        return startTime.isSameOrBefore(now);
    }

    static sortWeekdays(list: DictionaryDto[]): DictionaryDto[] {
        const findWeekdayIndex = (day: string) => {
            return parseInt(
                Object.keys(weekdaysMapping).find((k) => weekdaysMapping[k] === day),
                10,
            );
        };
        return list.sort((a, b) => {
            const aIndex = findWeekdayIndex(a.staticCode);
            const bIndex = findWeekdayIndex(b.staticCode);
            return aIndex - bIndex;
        });
    }

    static uniqDatesList(original: Date[]): Date[] {
        return original.filter((date, index, self) => {
            return self.findIndex((d) => date.getTime() === d.getTime()) === index;
        });
    }

    static datesEquality(first: Date, second: Date): boolean {
        if (!first || !second) {
            return false;
        }

        const firstDate = cloneDeep(first);
        const secondDate = cloneDeep(second);
        firstDate.setHours(0, 0, 0, 0);
        secondDate.setHours(0, 0, 0, 0);
        return firstDate.getTime() === secondDate.getTime();
    }
}
